import { useState, useEffect } from 'react'
import { Plus, Edit, Power, PowerOff, Search, X, User, MapPin, Settings, Link as LinkIcon, Image, Upload, ArrowRight, EyeOff, Eye } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import organizadoresService from '../../services/organizadoresService'
import clientesService from '../../services/clientesService'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { formatCpfCnpj, formatCep } from '../../utils/masks'
import { fetchCEP } from '../../utils/validators'

const fluxoOptions = [
  { value: 'comissao_primeira_parcela', label: 'Comissão na Primeira Parcela' },
  { value: 'gta_nota', label: 'GTA + Nota' },
  { value: 'comissao_separada', label: 'Comissão Separada' },
  { value: 'comissao_parcelada', label: 'Comissão Parcelada' },
  { value: 'comissao_cavalo', label: '🐴 Cavalo' }
]

export default function OrganizadoresList() {
  const navigate = useNavigate()
  const { switchToOrganizador } = useOrganizador()
  const [organizadores, setOrganizadores] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [statusFilter, setStatusFilter] = useState('')
  const [fluxoFilter, setFluxoFilter] = useState('')
  const [orderBy, setOrderBy] = useState('id')
  const [orderDir, setOrderDir] = useState('asc')
  const perPage = 25
  const [showModal, setShowModal] = useState(false)
  const [activeTab, setActiveTab] = useState('dados')
  const [editingOrganizador, setEditingOrganizador] = useState(null)
  const [cepLoading, setCepLoading] = useState(false)
  const [searchCidade, setSearchCidade] = useState('')
  const [cidadesSugeridas, setCidadesSugeridas] = useState([])
  const [showCidadeSugestoes, setShowCidadeSugestoes] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    cpf_cnpj: '',
    email: '',
    phone: '',
    phone2: '',
    address: '',
    cidade: '',
    estado: '',
    cep: '',
    fluxo_leilao: 'comissao_primeira_parcela',
    conta_azul_id: '',
    link_catalogo: '',
    logo_url: ''
  })

  useEffect(() => {
    loadOrganizadores(1, searchTerm, statusFilter, fluxoFilter)
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      loadOrganizadores(1, searchTerm, statusFilter, fluxoFilter)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    loadOrganizadores(1, searchTerm, statusFilter, fluxoFilter)
  }, [statusFilter, fluxoFilter, orderBy, orderDir])

  const loadOrganizadores = async (page = 1, search = '', status = '', fluxo = '') => {
    try {
      setLoading(true)
      const params = {
        page,
        per_page: perPage,
        order_by: orderBy,
        order_dir: orderDir
      }
      if (search) params.search = search
      if (status) params.status = status
      if (fluxo) params.fluxo = fluxo

      const response = await organizadoresService.getAll(params)
      setOrganizadores(response.data || [])
      setTotalPages(response.total_pages || 1)
      setTotal(response.total || 0)
      setCurrentPage(page)
    } catch (error) {
      console.error('Erro ao carregar organizadores:', error)
      setOrganizadores([])
      setTotalPages(1)
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (editingOrganizador) {
        await organizadoresService.update(editingOrganizador.id, formData)
      } else {
        await organizadoresService.create(formData)
      }
      await loadOrganizadores(currentPage, searchTerm, statusFilter, fluxoFilter)
      handleCloseModal()
    } catch (error) {
      console.error('Erro ao salvar organizador:', error)
    }
  }

  const handleEdit = (organizador) => {
    setEditingOrganizador(organizador)
    setFormData({
      name: organizador.name,
      cpf_cnpj: organizador.cpf_cnpj || '',
      email: organizador.email,
      phone: organizador.phone || '',
      phone2: organizador.phone2 || '',
      address: organizador.address || '',
      cidade: organizador.cidade || '',
      estado: organizador.estado || '',
      cep: organizador.cep || '',
      fluxo_leilao: organizador.fluxo_leilao || 'comissao_primeira_parcela',
      conta_azul_id: organizador.conta_azul_id || '',
      link_catalogo: organizador.link_catalogo || '',
      logo_url: organizador.logo_url ?
        (organizador.logo_url.startsWith('http') ? organizador.logo_url : `${import.meta.env.VITE_API_URL}${organizador.logo_url}`) :
        ''
    })
    setSearchCidade(organizador.cidade || '')
    setShowModal(true)
  }

  const handleCepChange = async (cep) => {
    const formattedCep = formatCep(cep)
    setFormData(prev => ({ ...prev, cep: formattedCep }))

    if (formattedCep.replace(/[^\d]/g, '').length === 8) {
      setCepLoading(true)
      try {
        const cepData = await fetchCEP(formattedCep)
        if (cepData) {
          setFormData(prev => ({
            ...prev,
            address: cepData.endereco || prev.address,
            cidade: cepData.cidade || prev.cidade,
            estado: cepData.estado || prev.estado
          }))
          setSearchCidade(cepData.cidade || '')
        }
      } finally {
        setCepLoading(false)
      }
    }
  }

  const handleCidadeInputChange = (valor) => {
    setSearchCidade(valor)
    setFormData(prev => ({ ...prev, cidade: valor }))
    setShowCidadeSugestoes(true)
  }

  const handleSelecionarCidade = (cidade) => {
    setFormData(prev => ({ ...prev, cidade: cidade.nome, estado: cidade.uf }))
    setSearchCidade(cidade.nome)
    setCidadesSugeridas([])
    setShowCidadeSugestoes(false)
  }

  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (showCidadeSugestoes && searchCidade && searchCidade.length >= 2) {
        try {
          const resultado = await clientesService.searchCidades(searchCidade)
          setCidadesSugeridas(resultado)
        } catch (err) {
          console.error('Erro ao buscar cidades:', err)
          setCidadesSugeridas([])
        }
      } else {
        setCidadesSugeridas([])
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchCidade, showCidadeSugestoes])

  const handleToggleStatus = async (organizador) => {
    try {
      if (organizador.is_active) {
        await organizadoresService.inactivate(organizador.id)
      } else {
        await organizadoresService.activate(organizador.id)
      }
      await loadOrganizadores(currentPage, searchTerm, statusFilter, fluxoFilter)
    } catch (error) {
      console.error('Erro ao alterar status:', error)
    }
  }

  const handleToggleSomenteVisualizacao = async (organizador) => {
    try {
      await organizadoresService.update(organizador.id, {
        somente_visualizacao: !organizador.somente_visualizacao
      })
      await loadOrganizadores(currentPage, searchTerm, statusFilter, fluxoFilter)
    } catch (error) {
      console.error('Erro ao alterar modo de visualização:', error)
    }
  }

  const handleGerenciar = (organizador) => {
    switchToOrganizador(organizador)
    navigate('/dashboard')
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingOrganizador(null)
    setFormData({
      name: '',
      cpf_cnpj: '',
      email: '',
      phone: '',
      phone2: '',
      address: '',
      cidade: '',
      estado: '',
      cep: '',
      fluxo_leilao: 'comissao_primeira_parcela',
      conta_azul_id: '',
      link_catalogo: '',
      logo_url: ''
    })
    setSearchCidade('')
    setCidadesSugeridas([])
  }

  const handlePageChange = (page) => {
    loadOrganizadores(page, searchTerm, statusFilter, fluxoFilter)
  }

  const renderPagination = () => {
    if (totalPages <= 1) return null

    const pages = []
    const maxVisible = 5
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages, start + maxVisible - 1)

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1)
    }

    for (let i = start; i <= end; i++) {
      pages.push(i)
    }

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
        <div className="flex justify-between flex-1 sm:hidden">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Anterior
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Próximo
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Mostrando <span className="font-medium">{(currentPage - 1) * perPage + 1}</span> até{' '}
              <span className="font-medium">{Math.min(currentPage * perPage, total)}</span> de{' '}
              <span className="font-medium">{total}</span> resultados
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50"
              >
                Anterior
              </button>
              {pages.map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border ${
                    page === currentPage
                      ? 'z-10 bg-rematei-blue border-rematei-blue text-white'
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50"
              >
                Próximo
              </button>
            </nav>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex justify-between items-center">
        <div className="hidden md:block">
          <h1 className="text-2xl font-bold text-gray-900">Organizadores</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie as leiloeiras cadastradas no sistema
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary md:ml-0 ml-auto"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Organizador
        </button>
      </div>

      {/* Search and Filters */}
      <div className="card-rematei">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <div className="relative sm:col-span-2 md:col-span-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar organizadores..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-rematei pl-10"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-rematei"
          >
            <option value="">Todos os status</option>
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
          </select>

          <select
            value={fluxoFilter}
            onChange={(e) => setFluxoFilter(e.target.value)}
            className="input-rematei"
          >
            <option value="">Todos os fluxos</option>
            {fluxoOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={`${orderBy}_${orderDir}`}
            onChange={(e) => {
              const [field, direction] = e.target.value.split('_')
              setOrderBy(field)
              setOrderDir(direction)
            }}
            className="input-rematei"
          >
            <option value="id_asc">ID (Crescente)</option>
            <option value="id_desc">ID (Decrescente)</option>
            <option value="name_asc">Nome (A-Z)</option>
            <option value="name_desc">Nome (Z-A)</option>
            <option value="email_asc">Email (A-Z)</option>
            <option value="email_desc">Email (Z-A)</option>
            <option value="created_at_asc">Data (Mais antigo)</option>
            <option value="created_at_desc">Data (Mais recente)</option>
          </select>
        </div>
      </div>

      {/* List */}
      <div className="card-rematei">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rematei-blue mx-auto"></div>
            <p className="mt-2 text-gray-500">Carregando...</p>
          </div>
        ) : organizadores.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Nenhum organizador encontrado</p>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="lg:hidden divide-y divide-gray-200">
              {organizadores.map((organizador) => (
                <div key={organizador.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <span className="text-xs text-gray-400">#{organizador.id}</span>
                      <div className="font-medium text-gray-900 truncate">{organizador.name}</div>
                      {organizador.cidade && (
                        <div className="text-xs text-gray-500">{organizador.cidade}/{organizador.estado}</div>
                      )}
                    </div>
                    <span className={`flex-shrink-0 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      organizador.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {organizador.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 space-y-0.5">
                    {organizador.email && <div className="truncate">{organizador.email}</div>}
                    {organizador.phone && <div>{organizador.phone}</div>}
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {organizador.fluxo_leilao?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleGerenciar(organizador)}
                      className="flex-1 bg-rematei-blue text-white px-3 py-2 rounded-md hover:bg-blue-700 text-xs font-medium flex items-center justify-center gap-1"
                    >
                      Gerenciar <ArrowRight className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => navigate(`/organizadores/${organizador.id}/detalhes`)}
                      className="p-2 text-rematei-blue hover:bg-blue-50 rounded-md"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleToggleSomenteVisualizacao(organizador)}
                      className={`p-2 rounded-md ${
                        organizador.somente_visualizacao
                          ? 'text-orange-500 hover:bg-orange-50'
                          : 'text-gray-400 hover:bg-gray-50'
                      }`}
                      title={organizador.somente_visualizacao ? 'Desativar modo somente visualização' : 'Ativar modo somente visualização'}
                    >
                      {organizador.somente_visualizacao ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleToggleStatus(organizador)}
                      className={`p-2 rounded-md ${
                        organizador.is_active
                          ? 'text-red-600 hover:bg-red-50'
                          : 'text-green-600 hover:bg-green-50'
                      }`}
                      title={organizador.is_active ? 'Inativar' : 'Ativar'}
                    >
                      {organizador.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table — table-fixed com larguras percentuais pra nunca
                exigir scroll horizontal nem reduzir sidebar/zoom pra caber */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full table-fixed divide-y divide-gray-200">
                <colgroup>
                  <col className="w-[6%]" />
                  <col className="w-[27%]" />
                  <col className="w-[27%]" />
                  <col className="w-[15%]" />
                  <col className="w-[9%]" />
                  <col className="w-[16%]" />
                </colgroup>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organizador</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contato</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fluxo</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {organizadores.map((organizador) => (
                    <tr key={organizador.id} className="hover:bg-gray-50">
                      <td className="px-3 py-4 align-top">
                        <div className="text-sm font-medium text-gray-900">#{organizador.id}</div>
                      </td>
                      <td className="px-3 py-4 align-top min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate" title={organizador.name}>{organizador.name}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {organizador.cpf_cnpj || '-'}
                          {organizador.cidade && ` · ${organizador.cidade}/${organizador.estado}`}
                        </div>
                      </td>
                      <td className="px-3 py-4 align-top min-w-0">
                        <div className="text-sm text-gray-500 truncate" title={organizador.email}>{organizador.email}</div>
                        {organizador.phone && <div className="text-xs text-gray-400 truncate">{organizador.phone}</div>}
                      </td>
                      <td className="px-3 py-4 align-top">
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 truncate max-w-full">
                          {organizador.fluxo_leilao?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                        </span>
                      </td>
                      <td className="px-3 py-4 align-top">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          organizador.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {organizador.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-3 py-4 align-top text-right text-sm font-medium">
                        <div className="flex justify-end items-center gap-1">
                          <button
                            onClick={() => handleGerenciar(organizador)}
                            className="text-rematei-blue hover:text-blue-700 hover:bg-blue-50 p-1.5 rounded"
                            title="Gerenciar"
                          >
                            <ArrowRight className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/organizadores/${organizador.id}/detalhes`)}
                            className="text-rematei-blue hover:text-blue-700 hover:bg-blue-50 p-1.5 rounded"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleSomenteVisualizacao(organizador)}
                            className={`p-1.5 rounded ${
                              organizador.somente_visualizacao
                                ? 'text-orange-500 hover:text-orange-600 hover:bg-orange-50'
                                : 'text-gray-400 hover:text-gray-500 hover:bg-gray-50'
                            }`}
                            title={organizador.somente_visualizacao ? 'Desativar modo somente visualização' : 'Ativar modo somente visualização'}
                          >
                            {organizador.somente_visualizacao ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleToggleStatus(organizador)}
                            className={`p-1.5 rounded ${
                              organizador.is_active
                                ? 'text-red-600 hover:text-red-700 hover:bg-red-50'
                                : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                            }`}
                            title={organizador.is_active ? 'Inativar' : 'Ativar'}
                          >
                            {organizador.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {renderPagination()}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end sm:items-center justify-center min-h-screen sm:px-4">
            <div className="fixed inset-0 bg-black opacity-50" onClick={handleCloseModal}></div>
            <div className="relative bg-white w-full sm:rounded-lg sm:max-w-4xl sm:max-h-[90vh] overflow-hidden rounded-t-2xl max-h-[95vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-base sm:text-lg font-medium text-gray-900">
                  {editingOrganizador ? 'Editar Organizador' : 'Novo Organizador'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Tabs — scrollable horizontalmente no mobile */}
              <div className="border-b border-gray-200 overflow-x-auto flex-shrink-0">
                <nav className="flex px-4 sm:px-6 min-w-max">
                  {[
                    { id: 'dados', label: 'Dados', icon: User },
                    { id: 'endereco', label: 'Endereço', icon: MapPin },
                    { id: 'configuracao', label: 'Config.', icon: Settings },
                    { id: 'integracoes', label: 'Integrações', icon: LinkIcon },
                    { id: 'logo', label: 'Logo', icon: Image },
                    ...(editingOrganizador ? [{ id: 'usuarios', label: 'Usuários', icon: User }] : [])
                  ].map((tab) => {
                    const Icon = tab.icon
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-1.5 py-3 px-3 sm:px-1 mr-2 sm:mr-6 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                          activeTab === tab.id
                            ? 'border-rematei-blue text-rematei-blue'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <span>{tab.label}</span>
                      </button>
                    )
                  })}
                </nav>
              </div>

              {/* Content */}
              <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                  {/* Dados Básicos */}
                  {activeTab === 'dados' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nome da Leiloeira *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="input-rematei"
                          placeholder="Ex: Leilões Central"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          CPF/CNPJ
                        </label>
                        <input
                          type="text"
                          value={formData.cpf_cnpj}
                          onChange={(e) => {
                            const formatted = formatCpfCnpj(e.target.value)
                            setFormData({...formData, cpf_cnpj: formatted})
                          }}
                          className="input-rematei"
                          placeholder="000.000.000-00 ou 00.000.000/0000-00"
                          maxLength="18"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email *
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="input-rematei"
                          placeholder="contato@leiloeira.com.br"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Telefone Principal
                          </label>
                          <input
                            type="text"
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                            className="input-rematei"
                            placeholder="(00) 00000-0000"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Telefone 2
                          </label>
                          <input
                            type="text"
                            value={formData.phone2}
                            onChange={(e) => setFormData({...formData, phone2: e.target.value})}
                            className="input-rematei"
                            placeholder="(00) 00000-0000"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Endereço */}
                  {activeTab === 'endereco' && (
                    <div className="space-y-4">
                      <div className="sm:w-1/2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          CEP
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={formData.cep}
                            onChange={(e) => handleCepChange(e.target.value)}
                            className="input-rematei"
                            placeholder="00000-000"
                            maxLength="9"
                          />
                          {cepLoading && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full" />
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          Preenche endereço, cidade e estado automaticamente
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Endereço Completo
                        </label>
                        <textarea
                          value={formData.address}
                          onChange={(e) => setFormData({...formData, address: e.target.value})}
                          className="input-rematei"
                          rows="3"
                          placeholder="Rua, número, bairro, complemento..."
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="relative">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cidade
                          </label>
                          <input
                            type="text"
                            value={searchCidade}
                            onChange={(e) => handleCidadeInputChange(e.target.value)}
                            onFocus={() => setShowCidadeSugestoes(true)}
                            onBlur={() => setTimeout(() => setShowCidadeSugestoes(false), 150)}
                            className="input-rematei"
                            placeholder="Nome da cidade"
                            autoComplete="off"
                          />
                          {showCidadeSugestoes && cidadesSugeridas.length > 0 && (
                            <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                              {cidadesSugeridas.map((cidade, index) => (
                                <li
                                  key={`${cidade.nome}-${cidade.uf}-${index}`}
                                  onMouseDown={() => handleSelecionarCidade(cidade)}
                                  className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer flex justify-between"
                                >
                                  <span>{cidade.nome}</span>
                                  <span className="text-gray-400">{cidade.uf}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Estado
                          </label>
                          <input
                            type="text"
                            maxLength="2"
                            value={formData.estado}
                            onChange={(e) => setFormData({...formData, estado: e.target.value.toUpperCase()})}
                            className="input-rematei"
                            placeholder="UF"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Configuração */}
                  {activeTab === 'configuracao' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fluxo de Leilão
                        </label>
                        <select
                          value={formData.fluxo_leilao}
                          onChange={(e) => setFormData({...formData, fluxo_leilao: e.target.value})}
                          className="input-rematei"
                        >
                          {fluxoOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Define como será processada a comissão nos leilões
                        </p>
                        {formData.fluxo_leilao === 'comissao_cavalo' && (
                          <p className="text-xs text-amber-600 mt-2">
                            🐴 Organizadores com fluxo Cavalo só podem criar leilões do tipo Cavalo
                            (as demais opções de tipo de leilão ficam ocultas para eles).
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Integrações */}
                  {activeTab === 'integracoes' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ID Conta Azul
                        </label>
                        <input
                          type="text"
                          value={formData.conta_azul_id}
                          onChange={(e) => setFormData({...formData, conta_azul_id: e.target.value})}
                          className="input-rematei"
                          placeholder="ID da integração com Conta Azul"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Link do Catálogo
                        </label>
                        <input
                          type="url"
                          value={formData.link_catalogo}
                          onChange={(e) => setFormData({...formData, link_catalogo: e.target.value})}
                          className="input-rematei"
                          placeholder="https://catalogo.leiloeira.com.br"
                        />
                      </div>
                    </div>
                  )}

                  {/* Logo */}
                  {activeTab === 'logo' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Logo da Leiloeira
                        </label>
                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                          <div className="space-y-1 text-center">
                            <Upload className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="flex text-sm text-gray-600">
                              <label className="relative cursor-pointer bg-white rounded-md font-medium text-rematei-blue hover:text-blue-500 focus-within:outline-none">
                                <span>Fazer upload</span>
                                <input
                                  type="file"
                                  className="sr-only"
                                  accept="image/*"
                                  onChange={async (e) => {
                                    const file = e.target.files[0]
                                    if (file && editingOrganizador) {
                                      try {
                                        const result = await organizadoresService.uploadLogo(editingOrganizador.id, file)
                                        const newLogoUrl = `${import.meta.env.VITE_API_URL}${result.logo_url}`
                                        setFormData({...formData, logo_url: newLogoUrl})
                                        setEditingOrganizador({...editingOrganizador, logo_url: newLogoUrl})
                                        await loadOrganizadores(currentPage, searchTerm, statusFilter, fluxoFilter)
                                      } catch (error) {
                                        console.error('Erro no upload:', error)
                                        alert('Erro ao fazer upload da logo')
                                      }
                                    } else if (file && !editingOrganizador) {
                                      alert('Salve o organizador primeiro para fazer upload da logo')
                                    }
                                  }}
                                />
                              </label>
                              <p className="pl-1">ou arraste e solte</p>
                            </div>
                            <p className="text-xs text-gray-500">
                              PNG, JPG, GIF até 10MB
                            </p>
                          </div>
                        </div>

                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Ou insira URL da logo
                          </label>
                          <input
                            type="url"
                            value={formData.logo_url}
                            onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                            className="input-rematei"
                            placeholder="https://exemplo.com/logo.png"
                          />
                        </div>
                      </div>

                      {formData.logo_url && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                          <img
                            src={formData.logo_url}
                            alt="Preview da logo"
                            className="max-w-xs max-h-32 object-contain border border-gray-200 rounded bg-white p-2"
                            onError={(e) => { e.target.style.display = 'none' }}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Usuários */}
                  {activeTab === 'usuarios' && editingOrganizador && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h4 className="text-lg font-medium text-gray-900">Usuários do Organizador</h4>
                        <button
                          type="button"
                          className="btn-primary text-sm"
                          onClick={() => alert('Criar usuário - em desenvolvimento')}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Novo Usuário
                        </button>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="text-center text-gray-500">
                          <User className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm">Funcionalidade de usuários em desenvolvimento</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Em breve você poderá gerenciar usuários e permissões
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-4 sm:p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingOrganizador ? 'Atualizar' : 'Criar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
